import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { ApexOptions, ChartType, NgApexchartsModule } from 'ng-apexcharts';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { ChatService } from '../../chat.service';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { ActivitiesService } from 'app/company/activities/activities.service';
import { DynamicChartsComponent } from 'app/ui/dynamic-charts/dynamic-charts.component';
import { FormattingPipe } from 'app/modules/shared/services/formatting-data.pipe';
import { scheduleList } from 'app/company/tracker/new-tracker.service';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSelectSearchComponent } from 'app/ui/mat-select-search/mat-select-search.component';
import { TruncatePipe } from 'app/core/pipes/truncate.pipe';
import { TrackerFilterHelper } from 'app/company/tracker/tracker-filter.helper';
import {
    CreateTrackerFromChartDialogComponent,
    type CreateTrackerFromChartPayload,
} from './create-tracker-from-chart-dialog.component';
import { FullChartPageComponent } from 'app/company/dashboard/full-chart-page/full-chart-page.component';
import { environment } from 'environments/environment';

@Component({
    selector: 'app-message-chart',
    standalone: true,
    imports: [
        TranslocoModule,
        MatIconModule,
        MatButtonModule,
        MatRippleModule,
        MatTooltipModule,
        NgApexchartsModule,
        CommonModule,
        DynamicChartsComponent,
        FormattingPipe,
        DecimalPipe,
        DatePipe,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatSelectSearchComponent,
        FormsModule,
        ReactiveFormsModule,
        TruncatePipe,
    ],
    providers: [FormattingPipe, DecimalPipe, DatePipe],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './message-chart.component.html',
    styleUrls: ['./message-chart.component.scss'],
})
export class MessageChartComponent implements OnInit, AfterViewInit, OnDestroy {
    protected _unsubscribeAll = new Subject<void>();
    lang: string = 'en';
    currentIndex: number = 0;
    queries: any[] = [];
    isTracker: boolean = false;
    scheduleList: any[] = scheduleList;

    typeList: any[] = [
        { id: 1, value: 'Time Series Tracker (Line Chart)' },
        { id: 2, value: 'Category Tracker (Bar Chart)' },
        { id: 3, value: 'Percentage Tracker(Pie Chart)' },
        { id: 4, value: 'Single Quantity Tracker(Single Value)' },
    ];

    public typeCtrl: FormControl<any> = new FormControl<any>(null);
    public scheduleCtrl: FormControl<any> = new FormControl<any>(null);
    secondFormGroup: FormGroup;
    expandedQueryIndices = new Set<number>();
    private _sliderResizeObserver: ResizeObserver | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private _dialogRef: MatDialogRef<MessageChartComponent>,
        private _changeDetectorRef: ChangeDetectorRef,
        private _translocoService: TranslocoService,
        private _router: Router,
        private _chatService: ChatService,
        private _fb: FormBuilder,
        private _activityService: ActivitiesService,
        private _alertService: CustomAlertService,
        private _elementRef: ElementRef<HTMLElement>,
        private _dialog: MatDialog
    ) {}

    ngOnInit() {
        this.secondFormGroup = this._fb.group({
            generatedQuery: [
                '',
                [Validators.required, Validators.maxLength(2000)],
            ],
            threshold: [null],
            thresholdType: [null],
        });
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((res) => {
                this.lang = res;
                this._changeDetectorRef.markForCheck();
            });

        if (
            this.data.from === 'chat' ||
            this.data.from === 'incorrect-messages'
        ) {
            this.queries = (this.data.chartData?.queries ?? []).filter(
                item => this.queryHasCharts(item)
            );
            this.queries.forEach(query => this.normalizeQueryCharts(query));
            this._changeDetectorRef.markForCheck();
        } else if (this.data.from === 'activity' || this.data.from === 'report') {
            const raw = this.extractActivityChartQueries(this.data.chartData);
            this.queries = raw.filter(item => this.queryHasCharts(item));
            this.queries.forEach(query => this.normalizeQueryCharts(query));
            this._changeDetectorRef.markForCheck();
        }
        window['Apex'] = {
            chart: {
                events: {
                    mounted: (chart: any, options?: any): void => {
                        this._fixSvgFill(chart.el);
                    },
                    updated: (chart: any, options?: any): void => {
                        this._fixSvgFill(chart.el);
                    },
                },
            },
        };
        this._changeDetectorRef.markForCheck();
    }

    ngAfterViewInit(): void {
        this._bindSliderResizeObserver();
        this._triggerChartRender();
    }

    /** Activity API may pass queries[], or nested subactivity; charts may be a single object or an array. */
    private extractActivityChartQueries(chartData: any): any[] {
        if (!chartData) {
            return [];
        }
        if (Array.isArray(chartData)) {
            return chartData;
        }
        if (chartData.question?.queries) {
            return chartData.question.queries;
        }
        if (chartData.data?.subactivity?.question?.queries) {
            return chartData.data.subactivity.question.queries;
        }
        return [];
    }

    private queryHasCharts(item: any): boolean {
        const c = item?.charts;
        if (c == null) {
            return false;
        }
        return Array.isArray(c) ? c.length > 0 : true;
    }

    /**
     * Build `chartsList`: every chart in `query.charts` (array or single object)
     * with `parsedData` set for dynamic-charts. Multiple charts per query are all shown.
     */
    private normalizeQueryCharts(query: any): void {
        if (!query?.charts) {
            query.chartsList = [];
            return;
        }
        const list = Array.isArray(query.charts)
            ? query.charts.filter(Boolean)
            : [query.charts];
        list.forEach((c) => {
            if (c) {
                c.parsedData = c.data;
            }
        });
        query.chartsList = list;
    }

    getChartDisplayTitle(chart: any): string {
        if (!chart) {
            return '';
        }
        if (chart.title) {
            return chart.title;
        }
        const x = chart.xAxis || '';
        const y = chart.yAxis || '';
        return x || y ? `${x} vs ${y}` : '';
    }

    getChartTitleForDynamic(chart: any): string {
        if (!chart) {
            return '';
        }
        if (chart.title) {
            return chart.title;
        }
        if (chart.xAxis) {
            return chart.yAxis ? `${chart.xAxis} vs ${chart.yAxis}` : chart.xAxis;
        }
        return chart.yAxis ? `X-Axis vs ${chart.yAxis}` : 'Y-Axis';
    }

    expandChart(chart: any, query?: any): void {
        const fallbackAgent = { name: 'Agent' };
        const normalizedChart = {
            ...chart,
            tableName: chart?.tableName ?? query?.tableName ?? '',
            question: chart?.question ?? query?.question ?? '',
            message: chart?.message ?? query?.message ?? '',
            agent: chart?.agent ?? query?.agent ?? this.data?.agent ?? fallbackAgent,
            createdAt: chart?.createdAt ?? query?.createdAt ?? new Date().toISOString(),
            sessionId: chart?.sessionId ?? this.data?.sessionId ?? null,
            messageId: chart?.messageId ?? this.data?.messageId ?? null,
            questionId: chart?.questionId ?? query?.id ?? null,
            subActivityId: chart?.subActivityId ?? this.data?.subActivityId ?? null,
        };

        this._dialog.open(FullChartPageComponent, {
            width: '90%',
            maxHeight: '90dvh',
            panelClass: 'biggDialog',
            data: {
                chart: normalizedChart,
                from: this.data?.from || 'chat',
            },
        });
    }

    makeItTracker(query) {
        // this.isTracker = true;
        // this.secondFormGroup.get('generatedQuery').setValue(query.query);
        // this._changeDetectorRef.markForCheck();
    }

    createTracker(query) {}
    cancelTracker() {
        this.isTracker = false;
        this._changeDetectorRef.markForCheck();
    }
    onSelectType(value) {}

    onSelectSchedule(value) {}

    toggleQueryExpanded(index: number): void {
        if (this.expandedQueryIndices.has(index)) {
            this.expandedQueryIndices.delete(index);
        } else {
            this.expandedQueryIndices.add(index);
        }
        this.expandedQueryIndices = new Set(this.expandedQueryIndices);
        this._changeDetectorRef.markForCheck();
    }

    isQueryExpanded(index: number): boolean {
        return this.expandedQueryIndices.has(index);
    }

    isQueryLong(query: string): boolean {
        return query && query.length > 150;
    }

    getChartOptions(query): ApexOptions {
        const parsedData = query.charts.parsedData;
        const labels = parsedData.map(item => item[query.charts.xAxis]);
        const seriesData = parsedData.map(item =>
            parseFloat(item[query.charts.yAxis])
        );
        const commonOptions: ApexOptions = {
            chart: {
                fontFamily: 'inherit',
                foreColor: 'inherit',
                height: 300,
                type: query.charts.graphType,
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true,
                    },
                },
                zoom: { enabled: false },
            },
            colors: ['var(--c2', 'var(--c1)'],
            dataLabels: {
                enabled: false,
                offsetY: -10,
                style: {
                    fontSize: '14px',
                    colors: ['#666'],
                },
                background: {
                    enabled: false,
                },
            },
            grid: {
                show: true,
                borderColor: 'rgba(128, 128, 128, 0.5)',
                strokeDashArray: 0,
            },
            labels: labels,
            tooltip: {
                enabled: true,
                followCursor: true,
                theme: 'dark',
                y: {
                    formatter: val =>
                        new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }).format(val),
                    title: {
                        formatter: seriesName => `${seriesName}: `,
                    },
                },
            },
            xaxis: {
                categories: labels,
                axisBorder: { show: true },
                axisTicks: { color: 'var(--fuse-border)' },
                labels: {
                    show: false,
                    style: {
                        colors: 'var(--fuse-text-secondary)',
                        fontSize: '12px',
                    },
                    rotate: -45,
                    rotateAlways: true,
                },
            },

            yaxis: {
                labels: {
                    offsetX: 0,
                    style: {
                        colors: 'var(--fuse-text-secondary)',
                        fontSize: '12px',
                        fontWeight: 'bold',
                    },
                    formatter: function (value: string | number) {
                        // Ensure value is of type string or number
                        // Check if the value is a number
                        if (typeof value === 'number') {
                            // If the number is a decimal, format it to 2 decimal places
                            return value % 1 !== 0
                                ? value.toFixed(2)
                                : value.toString();
                        }

                        // Check if the value is a valid date
                        const date = new Date(value);
                        if (!isNaN(date.getTime())) {
                            // Format the date as DD/MM/YYYY
                            const day = ('0' + date.getDate()).slice(-2);
                            const month = ('0' + (date.getMonth() + 1)).slice(
                                -2
                            ); // Months are zero-based
                            const year = date.getFullYear();
                            return `${day}/${month}/${year}`;
                        }

                        // If it's a string, return it as is
                        if (typeof value === 'string') {
                            return value;
                        }

                        // Unreachable for the declared `string | number`, but ApexCharts calls
                        // this formatter with whatever is in the series. Falling off the end
                        // returned `undefined` and the axis label rendered blank.
                        return String(value);
                    },
                },
            },
        };

        switch (query.charts.graphType) {
            case 'bar':
                return {
                    ...commonOptions,
                    plotOptions: {
                        bar: {
                            columnWidth: '70%',
                            dataLabels: { position: 'top' },
                        },
                    },
                    series: [
                        {
                            name: query.charts.yAxis,
                            data: seriesData,
                        },
                    ],
                };
            case 'line':
                return {
                    ...commonOptions,
                    series: [{ name: query.charts.yAxis, data: seriesData }],
                };
            case 'pie':
                return {
                    ...commonOptions,
                    chart: { ...commonOptions.chart, type: 'polarArea' },
                    series: seriesData,
                };
            default:
                return commonOptions;
        }
    }

    private _fixSvgFill(element: Element): void {
        const currentURL = this._router.url;
        Array.from(element.querySelectorAll('*[fill]'))
            .filter(el => el.getAttribute('fill').indexOf('url(') !== -1)
            .forEach((el) => {
                const attrVal = el.getAttribute('fill');
                el.setAttribute(
                    'fill',
                    `url(${currentURL}${attrVal.slice(attrVal.indexOf('#'))}`
                );
            });
    }

    prevSlide() {
        if (this.currentIndex > 0) {
            this.isTracker = false;
            --this.currentIndex;
            this._changeDetectorRef.markForCheck();
            // Trigger chart render after slide change
            this._triggerChartRender();
        }
    }

    nextSlide() {
        if (this.currentIndex < this.queries.length - 1) {
            this.isTracker = false;
            ++this.currentIndex;
            this._changeDetectorRef.markForCheck();
            // Trigger chart render after slide change
            this._triggerChartRender();
        }
    }

    private _triggerChartRender(): void {
        const run = (): void => {
            window.dispatchEvent(new Event('resize'));
            this._changeDetectorRef.markForCheck();
            this._adjustContainerHeight();
        };
        setTimeout(run, 100);
        setTimeout(run, 450);
    }

    private _bindSliderResizeObserver(): void {
        const host = this._elementRef.nativeElement;
        const ro = new ResizeObserver(() => {
            this._adjustContainerHeight();
            this._changeDetectorRef.markForCheck();
        });
        host.querySelectorAll('.slide').forEach(el => ro.observe(el));
        const slider = host.querySelector('.slider-content');
        if (slider) {
            ro.observe(slider);
        }
        this._sliderResizeObserver = ro;
    }

    private _adjustContainerHeight(): void {
        const host = this._elementRef.nativeElement;
        const container = host.querySelector(
            '.slider-content'
        ) as HTMLElement | null;

        if (container) {
            container.style.minHeight = '';
        }
    }

    /** Activity: PUT `activity/mark-as-favorite-chart`. Chat (and other session-based UIs): PUT `ai-chat/mark-as-favorite-chart`. */
    canFavoriteChart(): boolean {
        if (this.data?.from === 'activity') {
            return this.data.subActivityId != null;
        }
        return (
            this.data.sessionId != null && this.data.messageId != null
        );
    }

    /** Tracker CTA only for chat / activity in non-production builds. */
    showCreateTrackerButton(): boolean {
        if (environment.production) {
            return false;
        }
        return this.data?.from === 'chat' || this.data?.from === 'activity';
    }

    openCreateTrackerDialog(query: any, chart: any): void {
        const initial = this.buildCreateTrackerFromChartPayload(query, chart);
        this._dialog.open(CreateTrackerFromChartDialogComponent, {
            width: 'min(96vw, 1180px)',
            maxWidth: '96vw',
            maxHeight: '90dvh',
            panelClass: 'biggDialog',
            autoFocus: false,
            data: {
                initial,
                sourceQuery: query,
                sourceChart: chart,
                agentDataSources:
                    this.data?.chartData?.agent?.dataSources ??
                    this.data?.agent?.dataSources ??
                    [],
            },
        });
    }

    private buildCreateTrackerFromChartPayload(
        query: any,
        chart: any
    ): CreateTrackerFromChartPayload {
        const rawFilters = chart?.appliedFilters ?? query?.appliedFilters ?? [];
        const filters = TrackerFilterHelper.normalizeFilters(rawFilters);
        const selectedItems = TrackerFilterHelper.toApiFormat(filters);

        const type = this.mapGraphTypeToTrackerType(
            this.resolveChartGraphType(chart)
        );

        const title = this.getChartDisplayTitle(chart);
        const nameBase =
            (title || query?.tableName || 'Tracker').trim() || 'Tracker';

        const metricColumns = this.deriveMetricColumns(query, chart);

        return {
            chartId: Number(chart?.id ?? chart?.chartId ?? 0),
            query: String(query?.query ?? ''),
            dbType: String(query?.dataType ?? query?.dbType ?? ''),
            name: nameBase.slice(0, 500),
            dataSource: this.resolveDataSourceId(query, chart),
            table: String(query?.tableName ?? query?.table ?? ''),
            isExternal: this.resolveIsExternal(query, chart),
            type,
            period: 'daily',
            threshold: '',
            thresholdType: '',
            question: String(query?.question ?? ''),
            query_explanation: String(
                query?.query_explanation ?? query?.explanation ?? ''
            ),
            canvasId: '',
            dateColumn: String(chart?.dateColumn ?? query?.dateColumn ?? ''),
            start_date: String(query?.start_date ?? chart?.start_date ?? ''),
            end_date: String(query?.end_date ?? chart?.end_date ?? ''),
            metricColumns,
            selectedItems,
        };
    }

    private resolveIsExternal(query: any, chart: any): boolean {
        if (query?.isExternal === true || chart?.isExternal === true) {
            return true;
        }

        const fromDataSourceObject = (ds: unknown): boolean | null => {
            if (!ds || typeof ds !== 'object') {
                return null;
            }
            const type = String(
                (ds as Record<string, unknown>).type ?? ''
            ).toLowerCase();
            if (type === 'external') {
                return true;
            }
            if (type === 'internal') {
                return false;
            }
            return null;
        };

        for (const source of [
            this.data?.chartData?.dataSource,
            query?.dataSource,
            chart?.dataSource,
        ]) {
            const resolved = fromDataSourceObject(source);
            if (resolved !== null) {
                return resolved;
            }
        }

        const dataSourceId = this.resolveDataSourceId(query, chart);
        const agentSources =
            this.data?.chartData?.agent?.dataSources ??
            this.data?.agent?.dataSources ??
            [];
        if (dataSourceId && Array.isArray(agentSources)) {
            for (const ds of agentSources) {
                if (!ds || typeof ds !== 'object') {
                    continue;
                }
                const id = String(
                    (ds as Record<string, unknown>).eid ??
                        (ds as Record<string, unknown>).id ??
                        ''
                ).trim();
                if (id && id === dataSourceId) {
                    const resolved = fromDataSourceObject(ds);
                    if (resolved !== null) {
                        return resolved;
                    }
                }
            }
        }

        return false;
    }

    private resolveDataSourceId(query: any, chart: any): string {
        const readValue = (value: unknown): string => {
            if (value == null) {
                return '';
            }
            if (typeof value === 'string' || typeof value === 'number') {
                const normalized = String(value).trim();
                return normalized &&
                    normalized !== 'undefined' &&
                    normalized !== 'null'
                    ? normalized
                    : '';
            }
            if (typeof value === 'object') {
                const obj = value as Record<string, unknown>;
                for (const key of [
                    'eid',
                    'id',
                    'dataSourceId',
                    'dataSource',
                    'datasourceId',
                ]) {
                    const nested = readValue(obj[key]);
                    if (nested) {
                        return nested;
                    }
                }
            }
            return '';
        };

        for (const source of [query, chart, this.data?.chartData]) {
            if (!source || typeof source !== 'object') {
                continue;
            }
            for (const key of [
                'dataSourceId',
                'datasourceId',
                'datasource_id',
                'data_source_id',
                'dsId',
                'ds_id',
                'DsEid',
                'dataSource',
                'datasource',
                'data_source',
            ]) {
                const resolved = readValue(source[key]);
                if (resolved) {
                    return resolved;
                }
            }
        }

        const tableName = String(
            query?.tableName ?? query?.table ?? chart?.tableName ?? ''
        ).trim();
        const agentSources =
            this.data?.chartData?.agent?.dataSources ??
            this.data?.agent?.dataSources ??
            [];
        if (!tableName || !Array.isArray(agentSources)) {
            return '';
        }

        for (const ds of agentSources) {
            if (!ds || typeof ds !== 'object') {
                continue;
            }
            const dsId = readValue(ds.eid ?? ds.id);
            if (!dsId) {
                continue;
            }
            const lists = [ds.tables, ds.external_tables];
            const hasTable = lists.some(list =>
                Array.isArray(list) &&
                list.some((table) => {
                    if (typeof table === 'string') {
                        return table === tableName;
                    }
                    if (table && typeof table === 'object') {
                        return (
                            table.name === tableName ||
                            table.value === tableName ||
                            table.tableName === tableName
                        );
                    }
                    return false;
                })
            );
            if (hasTable) {
                return dsId;
            }
        }

        return readValue(query?.dataSourceName);
    }

    /** API chart type: `graphType` or `series.default[].type` (line | bar | pie). */
    private resolveChartGraphType(chart: any): string {
        if (!chart) {
            return '';
        }

        const fromGraphType = String(chart.graphType ?? '')
            .toLowerCase()
            .trim();
        if (fromGraphType) {
            return fromGraphType;
        }

        const series = chart.series;
        if (!series || typeof series !== 'object') {
            return '';
        }

        const defaultSeries = Array.isArray(series.default)
            ? series.default
            : [];
        if (defaultSeries.length > 0) {
            const fromDefault = String(defaultSeries[0]?.type ?? '')
                .toLowerCase()
                .trim();
            if (fromDefault) {
                return fromDefault;
            }
        }

        for (const key of Object.keys(series)) {
            const entries = Array.isArray(series[key]) ? series[key] : [];
            if (!entries.length) {
                continue;
            }
            const fromSeries = String(entries[0]?.type ?? '')
                .toLowerCase()
                .trim();
            if (fromSeries) {
                return fromSeries;
            }
        }

        return '';
    }

    private mapGraphTypeToTrackerType(graphType: string): number {
        if (graphType === 'line') {
            return 1;
        }
        if (graphType === 'bar') {
            return 2;
        }
        if (graphType === 'pie' || graphType === 'polararea') {
            return 3;
        }
        return 0;
    }

    private deriveMetricColumns(query: any, chart: any): string[] {
        const m = query?.metricColumns;
        if (Array.isArray(m) && m.length) {
            return m.map(x => String(x));
        }
        if (chart?.yAxis) {
            return [String(chart.yAxis)];
        }
        return [];
    }

    toggleFavorite(query: any, queryIndex: number, chart: any) {
        if (!chart?.id) {
            return;
        }
        const nextFavorite = !chart.favorite;

        const applyFavoriteLocally = (): void => {
            chart.favorite = nextFavorite;
            const q = this.queries[queryIndex];
            if (Array.isArray(q?.charts)) {
                const idx = q.charts.findIndex((c: any) => c?.id === chart.id);
                if (idx >= 0) {
                    q.charts[idx] = { ...q.charts[idx], favorite: nextFavorite };
                }
            } else if (q?.charts?.id === chart.id) {
                q.charts = { ...q.charts, favorite: nextFavorite };
            }
            if (q?.chartsList?.length) {
                q.chartsList = q.chartsList.map((c: any) =>
                    c?.id === chart.id ? { ...c, favorite: nextFavorite } : c
                );
            }
            this._changeDetectorRef.markForCheck();
        };

        if (this.data.from === 'activity' && this.data.subActivityId != null) {
            const modal = {
                chartId: chart.id,
                favorite: nextFavorite,
                subActivityId: this.data.subActivityId,
            };
            this._activityService
                .markActivityChartAsFavorite(modal)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    () => {
                        applyFavoriteLocally();
                    },
                    (error) => {
                        this._alertService.failedMessage(
                            error?.error?.message ?? 'Failed to update favorite'
                        );
                    }
                );
        } else if (
            this.data.sessionId != null &&
            this.data.messageId != null
        ) {
            const modal = {
                sessionId: this.data.sessionId,
                messageId: this.data.messageId,
                chartId: chart.id,
                favorite: nextFavorite,
            };
            this._chatService
                .markChartAsFavorite(modal)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    () => {
                        applyFavoriteLocally();
                    },
                    (error) => {
                        this._alertService.failedMessage(
                            error?.error?.message ?? 'Failed to update favorite'
                        );
                    }
                );
        }
    }
    ngOnDestroy(): void {
        this._sliderResizeObserver?.disconnect();
        this._sliderResizeObserver = null;
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
